import { lazy, Suspense, useState, useCallback, useEffect, useRef } from "react";
import appConfig from "../appConfig";
import {
  apiService,
  ApiStatus,
  CompanyDetails,
  CustomizeMicrositePayload,
  MicroSiteTemplate,
  MicroSiteV3Template
} from "../api";
import { GenericIcon } from "../icons";
import testingV3Data from "../components/dynamic/TestingV3DummyData.json";

const MicrositeLandingPageV2 = lazy(() => import("./MicrositeLandingPageV2"));
const MicrositeLandingPageV3 = lazy(() => import("./MicrositeLandingPageV3"));
const SlidesLandingPage = lazy(() => import("./SlidesLandingPage"));

const POLL_INTERVAL_MS = 3000;

// Wrapper component that handles polling and template fetching
const LandingPageWrapper = () => {
  const [isFetchingTemplate, setIsFetchingTemplate] = useState(true);
  const [template, setTemplate] = useState<MicroSiteTemplate | MicroSiteV3Template>();
  const [error, setError] = useState("");
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [isSlides, setIsSlides] = useState(false);

  const intervalIdRef = useRef<number>();
  const regenerateTriggeredRef = useRef(false);
  const numErrors = useRef(0);

  const [regenerationProps, setRegenerationProps] = useState<CustomizeMicrositePayload>({
    customPrompt: "",
    businessUnit: "",
    userRole: "",
    accountId: "",
    opportunityId: ""
  });

  useEffect(() => {
    const url = window.location.href;
    const urlObj = new URL(url);
    const companyName = urlObj.pathname.split("/")[1];
    if (companyName) {
      localStorage.setItem("microsite-company-name", companyName);
    }
  }, []);

  const companyDetailsRef = useRef<CompanyDetails>({
    companyName: appConfig.companyName,
    accountId: "",
    opportunityId: "",
    companyDomain: appConfig.companyName,
    isCompanyKnown: true,
    userRoles: ["Admin", "Manager", "User"],
    businessUnits: [
      "Data Analytics",
      "Sales",
      "Marketing",
      "Operations",
      "Fraud Management",
      "Data Security",
      "Product",
      "Finance"
    ]
  });

  const pollForStatus = useCallback(async () => {
    const { message, error, data } = await apiService.getCreateCompanyMicrositeStatus(appConfig.pollId);

    if (error || !data) {
      numErrors.current += 1;
      if (numErrors.current > 5) {
        window.clearTimeout(intervalIdRef.current);
        setIsFetchingTemplate(false);
        setError(message);
      } else {
        intervalIdRef.current = window.setTimeout(() => {
          pollForStatus();
        }, POLL_INTERVAL_MS);
      }
    } else {
      numErrors.current = 0;
      setProgressPercentage(data.progressPercentage || 0);

      // Update isSlides from the backend response
      if ((data as any).isSlides !== undefined) {
        setIsSlides(Boolean((data as any).isSlides));
        // To check is slides is working uncomment below
        // setIsSlides(true);
      }

      if (data.status === ApiStatus.COMPLETED) {
        companyDetailsRef.current = data.companyDetails || companyDetailsRef.current;
        const { enableDemo } = data;
        appConfig.enableDemo = appConfig.enableDemo || enableDemo;

        // Load dummy data for V3 instead of API data
        if (appConfig.enableV3) {
          setTemplate(testingV3Data.template as unknown as MicroSiteV3Template);
        } else {
          setTemplate(data.template);
        }
        setIsFetchingTemplate(false);
        setError("");
        regenerateTriggeredRef.current = false;
      } else if (data.status === ApiStatus.FAILED) {
        setIsFetchingTemplate(false);
        setError(data.errors?.join(",") || "Error creating content");
      } else {
        intervalIdRef.current = window.setTimeout(() => {
          pollForStatus();
        }, POLL_INTERVAL_MS);
      }
    }
  }, []);

  useEffect(() => {
    if (appConfig.pollId) {
      pollForStatus();
      return () => window.clearTimeout(intervalIdRef.current);
    }
  }, [pollForStatus]);

  const onRegenerate = useCallback(
    async (payload: CustomizeMicrositePayload) => {
      // if (typeof payload.isSlides !== "undefined") {
      //   setIsSlides(Boolean(payload.isSlides));
      // }
      setRegenerationProps(payload);
      setProgressPercentage(0);
      regenerateTriggeredRef.current = true;
      try {
        setIsFetchingTemplate(true);
        console.log("Sending regenerate request with pollId:", appConfig.pollId);
        const { error, data, message } = await apiService.regenerateCompanyMicrosite(
          companyDetailsRef.current.companyDomain,
          {
            ...payload,
            poll_id: appConfig.pollId,
            accountId: companyDetailsRef.current.accountId,
            opportunityId: companyDetailsRef.current.opportunityId
          }
        );

        if (error || !data) {
          setIsFetchingTemplate(false);
          setError(message);
          return;
        }

        // We no longer open a new tab; update pollId and continue loading in-place
        if (data.pollId) {
          appConfig.pollId = data.pollId;
          // restart polling for the new job
          pollForStatus();
        }
      } catch (err: any) {
        setIsFetchingTemplate(false);
        setError(err.message || "Error regenerating template");
      }
    },
    [pollForStatus]
  );

  // Loading state
  if (isFetchingTemplate) {
    return (
      <div className="flex flex-col items-center h-screen w-screen overflow-auto gap-10 bg-dark-gray-brand">
        <div className="flex-col flex gap-12 align-middle text-light-primary-text w-fit h-fit m-auto">
          <div className="flex text-red-brand text-[64px]">
            <GenericIcon
              className="mx-auto"
              iconName="bicycle-inline-red-lockup-rgb"
              size={64}
            />
          </div>

          <div className="flex flex-col gap-y-6 items-center justify-center">
            <div className="text-4xl max-md:text-center font-bold">Your 24x7 AI Analyst Copilot</div>
            <div className="text-lg font-semibold text-center max-w-lg">
              Bicycle&apos;s AI analyzes your operational data 24/7, provides insights on revenue-critical issues and
              opportunities — and recommends fast, precise action.
            </div>
          </div>

          <div className="flex flex-col items-center justify-center gap-y-4 w-full mx-auto">
            <div className="loader-container">
              <div className="loader">
                <svg
                  className="circle circle-left"
                  viewBox="0 0 24 24"
                >
                  <path
                    clipRule="evenodd"
                    d="M24.02 9.3C22.73 3.96 17.92 0 12.18 0C5.45 0 0 5.45 0 12.18C0 18.91 5.45 24.36 12.18 24.36C17.92 24.36 22.73 20.4 24.02 15.06H17.62C16.59 17.01 14.54 18.33 12.18 18.33C8.78 18.33 6.03 15.58 6.03 12.18C6.03 8.78 8.78 6.03 12.18 6.03C14.54 6.03 16.59 7.35 17.62 9.3H24.02Z"
                    fillRule="evenodd"
                  />
                </svg>
                <svg
                  className="circle circle-right"
                  viewBox="0 0 24 24"
                >
                  <path
                    clipRule="evenodd"
                    d="M24.02 9.3C22.73 3.96 17.92 0 12.18 0C5.45 0 0 5.45 0 12.18C0 18.91 5.45 24.36 12.18 24.36C17.92 24.36 22.73 20.4 24.02 15.06H17.62C16.59 17.01 14.54 18.33 12.18 18.33C8.78 18.33 6.03 15.58 6.03 12.18C6.03 8.78 8.78 6.03 12.18 6.03C14.54 6.03 16.59 7.35 17.62 9.3H24.02Z"
                    fillRule="evenodd"
                  />
                </svg>
                <div className="dot"></div>
              </div>
            </div>
            {regenerateTriggeredRef.current && (
              <div className="w-full h-4 rounded-full bg-gray-700 text-white">
                <div
                  className="h-full rounded-full bg-red-brand transition-all duration-300 ease-in-out"
                  style={{ width: `${progressPercentage}%` }}
                >
                  <span className="block px-2 text-xs font-semibold">{progressPercentage}%</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen h-full max-w-screen bg-dark-gray-brand">
        <div className="flex-col flex gap-12 align-middle text-light-primary-text w-fit h-fit m-auto">
          <div className="flex text-red-brand text-[64px]">
            <GenericIcon
              className="mx-auto"
              iconName="bicycle-inline-red-lockup-rgb"
              size={64}
            />
          </div>
          <ErrorJSX error={error} />
        </div>
      </div>
    );
  }

  // Render appropriate landing page based on isSlides flag
  if (isSlides) {
    return (
      <Suspense fallback={<div />}>
        <SlidesLandingPage
          companyDetails={companyDetailsRef.current}
          onRegenerate={payload => onRegenerate(payload)}
          regenerationProps={regenerationProps}
          template={template as MicroSiteTemplate}
        />
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<div />}>
      {appConfig.enableV3 === true ? (
        <MicrositeLandingPageV3
          companyDetails={companyDetailsRef.current}
          onRegenerate={onRegenerate}
          regenerationProps={regenerationProps}
          template={template as MicroSiteV3Template}
        />
      ) : (
        <MicrositeLandingPageV2
          companyDetails={companyDetailsRef.current}
          onRegenerate={onRegenerate}
          regenerationProps={regenerationProps}
          template={template as MicroSiteTemplate}
        />
      )}
    </Suspense>
  );
};

// Error component
const ErrorJSX = ({ error }: { error?: string }) => (
  <div className="max-w-2xl p-6 bg-red-50 rounded-lg border border-red-200">
    <div className="flex items-start gap-3">
      <svg
        className="h-6 w-6 text-red-600 mt-0.5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
        />
      </svg>

      <div className="flex-grow">
        <div className="text-lg font-semibold text-red-700">Unable to fetch content</div>
        <p className="mt-1 text-red-600">
          An error occurred while attempting to fetch content. This might be due to a temporary issue.
        </p>

        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <button
            className="inline-flex items-center justify-center px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors font-medium cursor-pointer"
            onClick={() => window.location.reload()}
          >
            <svg
              className="h-4 w-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
              />
            </svg>
            Try Again
          </button>
        </div>

        {error && (
          <div className="mt-4 p-3 bg-white rounded-md border border-red-100">
            <div className="text-sm text-gray-600">
              Error: {error}
              <br />
              Timestamp: {new Date().toLocaleString()}
            </div>
          </div>
        )}
      </div>
    </div>
  </div>
);

export default LandingPageWrapper;
